import React, {ComponentType} from 'react';
import {useAddToResourceHints} from './ResourceHintsProvider';
// eslint-disable-next-line monorepo/no-internal-import
import {HydrationBoundary} from '@aboutyou/ui-components/src/hooks/useHydrationDone/useHydrationDone';

export function lazy<T extends ComponentType<any>>(
  importer: () => Promise<{default: T}>,
  config?: {
    /**
     * The fallback component to show while the lazy-loaded module is loading.
     * If not provided, the Suspense Boundary will render nothing until the module is loaded.
     */
    fallback?: NonNullable<React.ReactNode> | null;
    /**
     * If true, the lazy-loaded module will be optimized for preloading as a priority resource.
     * Usually, this is only necessary for main Page/Route components.
     * Other modules should be downloaded lazily by react on demand when hydrating the page.
     */
    aboveTheFold?: boolean;
  },
) {
  const LazyLoaded = React.lazy(importer);

  const Suspensified = React.forwardRef(function Inner(props, ref) {
    if (!process.browser && config?.aboveTheFold) {
      // eslint-disable-next-line react-hooks/rules-of-hooks -- its okay break the rules here because the config.aboveTheFold is a constant, would never change for a given component
      useAddToResourceHints(importer.toString());
    }
    return (
      <React.Suspense fallback={config?.fallback ?? null}>
        <HydrationBoundary>
          <LazyLoaded {...(props as any)} ref={ref} />
        </HydrationBoundary>
      </React.Suspense>
    );
  });

  return Suspensified as unknown as T;
}
